import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLearnCtx } from "../components/Learn2/LearnPage";
import { selectionToMarkdown } from "../utils/learnUtils";
import {
  getFlatText,
  getRangeStartOffset,
  findBestMatch,
  clearHighlightsFromDOM,
  applyHighlightToDOM,
  makeHighlightRecord,
} from "../utils/highlightUtils";

/**
 * Shared highlighting logic for figure captions (FigureComp, IntFigureComp,
 * MultiFigureViewer, MultiFigureComp scroll-mode).
 *
 * @param {React.RefObject} containerRef
 *   Ref to the element that the user selects text within and that highlights
 *   are applied to.  Can be a mutable ref (e.g. an activeCaptionRef that is
 *   reassigned as the active panel changes) — the handlers all read
 *   containerRef.current lazily, so they always see the current element.
 *
 * @param {string|null} itemId
 *   Unique ID used as the paragraphId key in the highlight store.
 *   Pass null/undefined to disable highlighting (toolbar never appears).
 *
 * @returns {{
 *   onTypesetRef: React.RefObject,
 *   paraHighlights: object[],
 *   applyHighlights: (containerEl: Element) => void,
 *   toolbarPos: {x:number,y:number}|null,
 *   toolbarStyle: object|null,
 *   handleHighlight: (color: string) => void,
 *   handleRemoveHighlight: () => void,
 *   copyDone: boolean,
 *   copySelection: (e: Event) => void,
 *   preventDeselect: (e: Event) => void,
 * }}
 */
export function useCaptionHighlight(containerRef, itemId) {
  const { highlights, saveHighlight, deleteHighlight } = useLearnCtx();

  const [toolbarPos, setToolbarPos] = useState(null);
  const [copyDone, setCopyDone] = useState(false);
  const capturedSelRef = useRef(null);

  /* ── Highlights for this item only ── */
  const paraHighlights = useMemo(
    () => highlights.filter((h) => h.paragraphId === itemId),
    [highlights, itemId],
  );
  const paraHighlightsRef = useRef(paraHighlights);
  paraHighlightsRef.current = paraHighlights;

  /* ── MathJax integration ──
   * Pass the returned onTypesetRef as the third arg to useMathJax().
   * After each typeset, typesetVersion increments and highlights are
   * re-applied to containerRef.current. */
  const [typesetVersion, setTypesetVersion] = useState(0);
  const onTypesetRef = useRef(null);
  onTypesetRef.current = () => setTypesetVersion((v) => v + 1);

  const highlightsKey = paraHighlights
    .map((h) => `${h.id}:${h.color}`)
    .join("|");

  useEffect(() => {
    const container = containerRef.current;
    if (!itemId || typesetVersion === 0 || !container) return;
    clearHighlightsFromDOM(container);
    for (const h of paraHighlights) applyHighlightToDOM(container, h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesetVersion, highlightsKey]);

  /** Apply highlights to an additional container (e.g. a modal caption). */
  const applyHighlights = useCallback((containerEl) => {
    if (!containerEl) return;
    clearHighlightsFromDOM(containerEl);
    for (const h of paraHighlightsRef.current)
      applyHighlightToDOM(containerEl, h);
  }, []);

  /* ── Selection tracking ── */
  useEffect(() => {
    const onSelectionChange = () => {
      // Read containerRef.current lazily — works with mutable refs (e.g. activeCaptionRef)
      const container = containerRef.current;

      if (!itemId || !container) {
        capturedSelRef.current = null;
        setToolbarPos(null);
        return;
      }

      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        capturedSelRef.current = null;
        setToolbarPos(null);
        return;
      }

      if (
        !container.contains(sel.anchorNode) ||
        !container.contains(sel.focusNode)
      ) {
        capturedSelRef.current = null;
        setToolbarPos(null);
        return;
      }

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) {
        setToolbarPos(null);
        return;
      }

      const selText = sel.toString();
      const startOff = getRangeStartOffset(range, container);
      capturedSelRef.current =
        startOff !== null
          ? { start: startOff, end: startOff + selText.length, text: selText }
          : null;

      setToolbarPos({ x: rect.left + rect.width / 2, y: rect.top });
    };

    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
    // containerRef is a stable ref object; handler reads .current lazily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  /* Hide toolbar when the viewport scrolls */
  useEffect(() => {
    if (!toolbarPos) return;
    const hide = () => setToolbarPos(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, [toolbarPos]);

  /* ── Add highlight (merge same-colour / trim different-colour overlaps) ── */
  const handleHighlight = useCallback(
    (color) => {
      const captured = capturedSelRef.current;
      if (!itemId || !captured || !containerRef.current) return;
      if (!captured.text.trim()) return;

      const flat = getFlatText(containerRef.current);
      let newStart = captured.start;
      let newEnd = captured.end;

      const sorted = paraHighlightsRef.current
        .map((r) => {
          const hStart = findBestMatch(flat, r.exact, r.prefix, r.suffix);
          return hStart === -1
            ? null
            : { record: r, hStart, hEnd: hStart + r.exact.length };
        })
        .filter(Boolean)
        .sort((a, b) => a.hStart - b.hStart);

      for (const { record, hStart, hEnd } of sorted) {
        if (hEnd <= newStart || hStart >= newEnd) continue;
        deleteHighlight(record.id);
        if (record.color === color) {
          newStart = Math.min(newStart, hStart);
          newEnd = Math.max(newEnd, hEnd);
        } else {
          if (hStart < newStart)
            saveHighlight(makeHighlightRecord(record, flat, hStart, newStart));
          if (hEnd > newEnd)
            saveHighlight(makeHighlightRecord(record, flat, newEnd, hEnd));
        }
      }

      saveHighlight({
        paragraphId: itemId,
        color,
        exact: flat.slice(newStart, newEnd),
        prefix: flat.slice(Math.max(0, newStart - 40), newStart),
        suffix: flat.slice(newEnd, newEnd + 40),
      });

      window.getSelection()?.removeAllRanges();
      capturedSelRef.current = null;
      setToolbarPos(null);
    },
    [itemId, saveHighlight, deleteHighlight],
  );

  /* ── Remove / trim highlights overlapping the captured selection ── */
  const handleRemoveHighlight = useCallback(() => {
    const captured = capturedSelRef.current;
    if (!itemId || !captured || !containerRef.current) return;

    const { start: selStart, end: selEnd } = captured;
    const flat = getFlatText(containerRef.current);

    for (const record of paraHighlightsRef.current) {
      const hStart = findBestMatch(
        flat,
        record.exact,
        record.prefix,
        record.suffix,
      );
      if (hStart === -1) continue;
      const hEnd = hStart + record.exact.length;
      if (hEnd <= selStart || hStart >= selEnd) continue;

      deleteHighlight(record.id);
      const clipsStart = selStart <= hStart;
      const clipsEnd = selEnd >= hEnd;
      if (clipsStart && clipsEnd) {
        /* fully deleted */
      } else if (clipsStart)
        saveHighlight(makeHighlightRecord(record, flat, selEnd, hEnd));
      else if (clipsEnd)
        saveHighlight(makeHighlightRecord(record, flat, hStart, selStart));
      else {
        saveHighlight(makeHighlightRecord(record, flat, hStart, selStart));
        saveHighlight(makeHighlightRecord(record, flat, selEnd, hEnd));
      }
    }

    window.getSelection()?.removeAllRanges();
    capturedSelRef.current = null;
    setToolbarPos(null);
  }, [itemId, deleteHighlight, saveHighlight]);

  /* ── Copy ── */
  const copySelection = useCallback(async (e) => {
    e.stopPropagation();
    const text = selectionToMarkdown();
    try {
      await navigator.clipboard.writeText(text);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 1500);
    } catch {
      /* clipboard access denied */
    }
  }, []);

  const preventDeselect = useCallback((e) => e.preventDefault(), []);

  const toolbarStyle = toolbarPos
    ? {
        position: "fixed",
        left: `${toolbarPos.x}px`,
        top: `${toolbarPos.y - 8}px`,
        bottom: "auto",
        transform: "translateX(-50%) translateY(-100%)",
        zIndex: 50,
      }
    : null;

  return {
    onTypesetRef,
    paraHighlights,
    applyHighlights,
    toolbarPos,
    toolbarStyle,
    handleHighlight,
    handleRemoveHighlight,
    copyDone,
    copySelection,
    preventDeselect,
  };
}
